import React, { useState, useEffect } from "react";
import { Typography, Box, Alert, Button } from "@mui/material";
import { useSearchParams, useNavigate } from "react-router-dom";
import { verifyEmail } from "../../client/api";
import { getSafeRedirectTarget } from "../../utils/ssoCookie";

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"verifying" | "success" | "error">("verifying");
  const [error, setError] = useState<string>("");

  const token = searchParams.get("token");
  // Only present for a cross-app invite (farm-manager/zapflow) - embedded
  // by the backend into the verification email's own link at signup time
  // (see emailService.sendVerificationEmail's redirect param). Carrying it
  // to /login lets Login.tsx's existing redirect handling bounce the user
  // straight into the app they were invited to, instead of letslaunch's
  // generic app-selection screen.
  const redirectParam = getSafeRedirectTarget(searchParams.get("redirect"));

  useEffect(() => {
    const doVerify = async () => {
      if (!token) {
        setStatus("error");
        setError("Missing verification token");
        return;
      }

      try {
        await verifyEmail(token);
        setStatus("success");
        const loginUrl = redirectParam
          ? `/login?redirect=${encodeURIComponent(redirectParam)}`
          : "/login";
        setTimeout(() => navigate(loginUrl), 3000);
      } catch (err: any) {
        setStatus("error");
        setError(err.message || "Invalid or expired verification link");
      }
    };

    doVerify();
  }, [token, navigate, redirectParam]);

  return (
    <Box sx={{ maxWidth: 400, margin: "auto", padding: 4, textAlign: "center" }}>
      {status === "verifying" && <Typography>Verifying your email...</Typography>}

      {status === "success" && (
        <>
          <Alert severity="success">Email verified successfully! Redirecting to login...</Alert>
        </>
      )}

      {status === "error" && (
        <>
          <Alert severity="error">{error}</Alert>
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={() => navigate("/verify-email-pending")}>
              Resend Verification Email
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default VerifyEmail;
